/**
 * Week 4: Production useTheme Hook with Registry Integration
 * 
 * Integrates Week 3 component registry for:
 * - Type-safe component access
 * - Development warnings for missing components
 * - Default class fallbacks from registry
 * - Data attribute injection
 * 
 * Usage:
 *   const c = useTheme("BuyBlock");
 *   <button {...c("AddToCartButton", "w-full")}>Add to Cart</button>
 * 
 * Returns:
 *   { 
 *     className: "resolved-classes w-full", 
 *     "data-theme-section": "BuyBlock",
 *     "data-theme-component": "AddToCartButton"
 *   }
 */

import { useRootLoaderData } from "@/root";
import { useMemo } from "react";
import { cn } from "@core/lib/utils";
import type { ThemeSection, ThemeComponents } from "@/generated/theme-components";
import {
  getComponentDefaults,
  hasComponent,
  validateComponentUsage,
  findSimilarComponents,
} from "@/plugins/core/theme/ThemeComponentRegistry";

interface ThemeSettings {
  Global?: {
    Colors?: Record<string, string>;
    Typography?: Record<string, string>;
    Spacing?: Record<string, string>;
  };
  SectionThemes?: Record<string, {
    Colors?: Record<string, string>;
    Components?: Record<string, string | { CustomClasses?: string; GeneratedClasses?: string }>;
  }>;
  Sections?: Record<string, Record<string, string | { CustomClasses?: string; GeneratedClasses?: string }>>;
  DisableDefaultStyling?: boolean;
  UseStandardCustomClassesOverride?: boolean;
}

function readClassParts(input: unknown): { generatedClasses: string; customClasses: string } {
  if (!input) {
    return { generatedClasses: "", customClasses: "" };
  }
  if (typeof input === "string") {
    return { generatedClasses: "", customClasses: input };
  }
  if (typeof input === "object") {
    const generated = (input as any).GeneratedClasses;
    const custom = (input as any).CustomClasses;
    return {
      generatedClasses: typeof generated === "string" ? generated : "",
      customClasses: typeof custom === "string" ? custom : "",
    };
  }
  return { generatedClasses: "", customClasses: "" };
}

/**
 * Cascade Resolution: Component → Section → Registry → Default
 * 
 * Priority order:
 * 1. Base defaults (registry + inline default classes)
 * 2. Component-level generated classes
 * 3. Component-level custom classes
 * 4. Section-level override classes
 */
function resolveCascade<S extends ThemeSection>(
  section: S,
  component: ThemeComponents[S],
  defaultClasses: string,
  themeSettings: ThemeSettings | null
): string {
  const useStandardCustomClassesOverride = themeSettings?.UseStandardCustomClassesOverride ?? true;
  if (useStandardCustomClassesOverride) {
    // 1. Component-level customization (highest priority - full override)
    const componentCustom = themeSettings?.Sections?.[section]?.[component as string];
    if (componentCustom) {
      if (typeof componentCustom === 'string' && componentCustom) {
        return componentCustom;
      }
      // Extract CustomClasses from object (for backward compatibility)
      if (typeof componentCustom === 'object' && componentCustom !== null) {
        const customClasses = (componentCustom as any).CustomClasses;
        if (typeof customClasses === 'string' && customClasses) {
          return customClasses;
        }
      }
    }
    
    // 2. Section-level override (full override)
    const sectionOverride = themeSettings?.SectionThemes?.[section]?.Components?.[component as string];
    if (sectionOverride) {
      if (typeof sectionOverride === 'string' && sectionOverride) {
        return sectionOverride;
      }
      // Extract CustomClasses from object
      if (typeof sectionOverride === 'object' && sectionOverride !== null) {
        const customClasses = (sectionOverride as any).CustomClasses;
        if (typeof customClasses === 'string' && customClasses) {
          return customClasses;
        }
      }
    }
    
    // 3. Registry defaults (loaded by backend ApplyFallbacks at startup)
    // Skip if DisableDefaultStyling is enabled
    if (!themeSettings?.DisableDefaultStyling) {
      const registryDefaults = getComponentDefaults(section, component);
      if (registryDefaults) {
        return registryDefaults;
      }
    }
    
    // 4. Inline defaults (lowest priority - always works)
    // Skip if DisableDefaultStyling is enabled (blank canvas mode)
    return themeSettings?.DisableDefaultStyling ? "" : defaultClasses;
  }

  if (themeSettings?.DisableDefaultStyling) {
    const componentCustomOnly = readClassParts(themeSettings?.Sections?.[section]?.[component as string]);
    const sectionCustomOnly = readClassParts(
      themeSettings?.SectionThemes?.[section]?.Components?.[component as string]
    );
    return cn(componentCustomOnly.customClasses, sectionCustomOnly.customClasses);
  }

  const registryDefaults = getComponentDefaults(section, component);
  const componentClasses = readClassParts(themeSettings?.Sections?.[section]?.[component as string]);
  const sectionClasses = readClassParts(themeSettings?.SectionThemes?.[section]?.Components?.[component as string]);

  // Section-level classes still apply last so they can intentionally win conflicts.
  return cn(
    registryDefaults,
    defaultClasses,
    componentClasses.generatedClasses,
    componentClasses.customClasses,
    sectionClasses.customClasses
  );
}

/**
 * Week 4: Production useTheme Hook
 * 
 * Type-safe theme component accessor with cascade resolution.
 * Integrates with Week 3 component registry for automatic defaults.
 * 
 * @param section - Theme section name (type-safe from registry)
 * @returns Function to get theme classes for components
 */
export function useTheme<S extends ThemeSection>(section: S) {
  const { theme } = useRootLoaderData();
  
  return useMemo(() => {
    /**
     * Get theme classes for a component
     * 
     * @param component - Component name (type-safe for this section)
     * @param defaults - Optional inline default classes (fallback if not in registry)
     * @returns Props object with className and data attributes
     */
    return function c(
      component: ThemeComponents[S],
      defaults: string = ""
    ): {
      className: string;
      "data-theme-section": string;
      "data-theme-component": string;
    } {
      // Development-mode validation
      if (process.env.NODE_ENV === "development") {
        if (!hasComponent(section, component)) {
          const similar = findSimilarComponents(`${section}.${component}`, 3);
          // console.warn(
          //   `[useTheme] Component "${component}" not found in section "${section}"\n` +
          //   `Did you mean: ${similar.map(s => s.component).join(", ")}?`
          // );
        }
        
        validateComponentUsage(section, component);
      }
      
      // Resolve classes through cascade
      const className = resolveCascade(
        section,
        component,
        defaults,
        theme as ThemeSettings | null
      );
      
      return {
        className,
        "data-theme-section": section,
        "data-theme-component": component as string,
      };
    };
  }, [section, theme]);
}

/**
 * Hook to check if default styling is disabled (blank canvas mode)
 * 
 * @returns true if DisableDefaultStyling is enabled in theme settings
 */
export function useDisableDefaultStyling(): boolean {
  const { theme } = useRootLoaderData();
  return (theme as ThemeSettings)?.DisableDefaultStyling ?? false;
}

/**
 * Legacy alias for backward compatibility
 * Week 4: Consider deprecating in favor of direct useTheme
 */
export function useThemeComponent<S extends ThemeSection>(section: S) {
  return useTheme(section);
}
